import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const emptyGuide = {
  title: '', category: '', intro: '', isPublished: true, sortOrder: 0,
  steps: [],
};
const emptyStep = { title: '', image: '', caption: '', note: '', sortOrder: 0 };

export default function AdminMeasureGuides() {
  const [guides,  setGuides]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(emptyGuide);
  const [coverFile, setCoverFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [uploadingStep, setUploadingStep] = useState(null); // step index being uploaded

  const load = () => {
    setLoading(true);
    api.get('/guides/admin/all')
      .then(r => setGuides(r.data.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyGuide);
    setCoverFile(null);
    setModal(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({
      title:       g.title,
      category:    g.category || '',
      intro:       g.intro || '',
      isPublished: g.isPublished,
      sortOrder:   g.sortOrder || 0,
      steps:       g.steps?.map(s => ({ ...s })) || [],
    });
    setCoverFile(null);
    setModal(true);
  };

  const h = e => setForm(f => ({
    ...f,
    [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  // ── Step helpers ─────────────────────────────────────────────────────────
  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { ...emptyStep, sortOrder: f.steps.length }] }));

  const updateStep = (i, field, value) =>
    setForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [field]: value } : s) }));

  const removeStep = (i) => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const moveStep = (i, dir) => {
    setForm(f => {
      const arr = [...f.steps];
      const j   = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, steps: arr.map((s, idx) => ({ ...s, sortOrder: idx })) };
    });
  };

  // Upload step image immediately and store URL in step
  const uploadStepImage = async (i, file) => {
    if (!file) return;
    setUploadingStep(i);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/guides/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateStep(i, 'image', r.data.url);
      toast.success('Step image uploaded!');
    } catch { toast.error('Image upload failed.'); }
    finally { setUploadingStep(null); }
  };

  const save = async () => {
    if (!form.title) { toast.error('Title required.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title',       form.title);
      fd.append('category',    form.category);
      fd.append('intro',       form.intro);
      fd.append('isPublished', form.isPublished);
      fd.append('sortOrder',   form.sortOrder);
      fd.append('steps',       JSON.stringify(form.steps));
      if (coverFile) fd.append('image', coverFile);

      if (editing) {
        const r = await api.put(`/guides/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setGuides(gs => gs.map(g => g._id === editing._id ? r.data.data : g));
        toast.success('Guide updated!');
      } else {
        const r = await api.post('/guides', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setGuides(gs => [r.data.data, ...gs]);
        toast.success('Guide created!');
      }
      setModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this guide?')) return;
    try {
      await api.delete(`/guides/${id}`);
      setGuides(gs => gs.filter(g => g._id !== id));
      toast.success('Deleted.');
    } catch { toast.error('Failed.'); }
  };

  const togglePublished = async (g) => {
    try {
      const fd = new FormData();
      fd.append('title', g.title);
      fd.append('isPublished', !g.isPublished);
      fd.append('steps', JSON.stringify(g.steps || []));
      const r = await api.put(`/guides/${g._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setGuides(gs => gs.map(x => x._id === g._id ? r.data.data : x));
    } catch { toast.error('Failed.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Measure Guides</h1>
          <p className="font-body text-sm text-secondary mt-1">Step-by-step guides shown on the public site.</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <span className="material-symbols-outlined">add</span> New Guide
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 skeleton" />)}</div>
      ) : guides.length === 0 ? (
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-[64px] text-outline-variant">straighten</span>
          <p className="font-headline font-bold text-xl uppercase mt-3 text-secondary">No guides yet</p>
          <button onClick={openAdd} className="btn-primary mt-4">Create First Guide</button>
        </div>
      ) : (
        <div className="bg-white border border-outline-variant/20 overflow-x-auto">
          <table className="cf-table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Title</th>
                <th>Category</th>
                <th>Steps</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {guides.map(g => (
                <tr key={g._id}>
                  <td>
                    {g.coverImage
                      ? <img src={g.coverImage} alt={g.title} className="w-14 h-10 object-cover" />
                      : <div className="w-14 h-10 bg-surface-container flex items-center justify-center">
                          <span className="material-symbols-outlined text-[16px] text-outline-variant">image</span>
                        </div>
                    }
                  </td>
                  <td className="font-bold">{g.title}</td>
                  <td className="text-secondary text-sm">{g.category || '—'}</td>
                  <td className="text-secondary">{g.steps?.length || 0}</td>
                  <td>
                    <span className={`badge ${g.isPublished ? 'badge-green' : 'badge-red'}`}>
                      {g.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(g)} className="p-1.5 text-secondary hover:text-primary-container">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => togglePublished(g)} className="p-1.5 text-secondary hover:text-on-surface"
                        title={g.isPublished ? 'Unpublish' : 'Publish'}>
                        <span className="material-symbols-outlined text-[18px]">{g.isPublished ? 'visibility_off' : 'visibility'}</span>
                      </button>
                      <button onClick={() => del(g._id)} className="p-1.5 text-secondary hover:text-error">
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container sticky top-0 bg-white z-10">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Guide' : 'New Guide'}</h2>
              <button onClick={() => setModal(false)}><span className="material-symbols-outlined">close</span></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="input-label">Guide Title *</label>
                  <input name="title" value={form.title} onChange={h} className="input-field" placeholder="e.g. How to Measure a Bolt" />
                </div>
                <div>
                  <label className="input-label">Category Tag</label>
                  <input name="category" value={form.category} onChange={h} className="input-field" placeholder="e.g. Bolts, Nuts" />
                </div>
                <div>
                  <label className="input-label">Sort Order</label>
                  <input name="sortOrder" type="number" value={form.sortOrder} onChange={h} className="input-field" />
                </div>
                <div className="sm:col-span-2">
                  <label className="input-label">Introduction Text</label>
                  <textarea name="intro" value={form.intro} onChange={h} rows={3} className="input-field resize-none"
                    placeholder="Brief intro shown at top of guide..." />
                </div>
                <div>
                  <label className="input-label">Cover Image</label>
                  <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0])} className="input-field" />
                  {editing?.coverImage && !coverFile && (
                    <img src={editing.coverImage} alt="" className="mt-2 h-16 object-contain border" />
                  )}
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isPublished" checked={!!form.isPublished} onChange={h} className="w-4 h-4 accent-primary-container" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Published</span>
                  </label>
                </div>
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="input-label mb-0">Steps ({form.steps.length})</label>
                  <button type="button" onClick={addStep} className="flex items-center gap-1.5 text-xs font-label font-bold text-primary-container hover:underline">
                    <span className="material-symbols-outlined text-[16px]">add_circle</span> Add Step
                  </button>
                </div>

                <div className="space-y-4">
                  {form.steps.map((step, i) => (
                    <div key={i} className="border border-outline-variant/30 p-4 bg-surface-container-lowest">
                      {/* Step header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-primary-container text-white font-headline font-black text-sm flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </div>
                        <span className="font-label text-xs font-bold uppercase tracking-wider flex-1">Step {i + 1}</span>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0}
                            className="p-1 text-secondary hover:text-on-surface disabled:opacity-30">
                            <span className="material-symbols-outlined text-[16px]">arrow_upward</span>
                          </button>
                          <button type="button" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}
                            className="p-1 text-secondary hover:text-on-surface disabled:opacity-30">
                            <span className="material-symbols-outlined text-[16px]">arrow_downward</span>
                          </button>
                          <button type="button" onClick={() => removeStep(i)}
                            className="p-1 text-secondary hover:text-error">
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="input-label">Step Title *</label>
                          <input value={step.title} onChange={e => updateStep(i, 'title', e.target.value)}
                            className="input-field text-sm" placeholder="e.g. Measure the shank length" />
                        </div>

                        <div>
                          <label className="input-label">Step Image</label>
                          <div className="flex gap-2 items-center">
                            <input type="file" accept="image/*"
                              onChange={e => uploadStepImage(i, e.target.files[0])}
                              className="input-field text-sm flex-1" />
                            {uploadingStep === i && (
                              <span className="w-5 h-5 border-2 border-primary-container border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            )}
                          </div>
                          {step.image && (
                            <div className="mt-2 flex items-center gap-2">
                              <img src={step.image} alt="" className="h-16 object-contain border border-outline-variant/20" />
                              <button type="button" onClick={() => updateStep(i, 'image', '')}
                                className="text-xs text-error hover:underline">Remove</button>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="input-label">Caption</label>
                          <input value={step.caption} onChange={e => updateStep(i, 'caption', e.target.value)}
                            className="input-field text-sm" placeholder="Short caption under image" />
                        </div>

                        <div>
                          <label className="input-label">Note / Explanation</label>
                          <textarea value={step.note} onChange={e => updateStep(i, 'note', e.target.value)}
                            rows={2} className="input-field text-sm resize-none"
                            placeholder="Additional explanation for this step..." />
                        </div>
                      </div>
                    </div>
                  ))}

                  {form.steps.length === 0 && (
                    <div className="text-center py-8 border-2 border-dashed border-outline-variant/40">
                      <p className="font-label text-xs text-secondary uppercase tracking-widest">No steps yet</p>
                      <button type="button" onClick={addStep} className="btn-ghost mt-2">
                        <span className="material-symbols-outlined text-[16px]">add</span> Add First Step
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary">
                {saving
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  : <><span className="material-symbols-outlined">save</span> {editing ? 'Update' : 'Create'} Guide</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
